import { BaseIcon, Popover } from "@shltelemedicine/shl-ui-lib";
import linkedApproverUserIcon from "../../../../../assets/management/linked_approve_users.svg";
import linkedUserIcon from "../../../../../assets/management/link_users.svg";
import { IStudyUserTreeItem } from "../../../../../models/study.interface";

import { Button, Spinner } from "react-bootstrap";
import linkUserIcon from "../../../../../assets/management/link_user.svg";
import unlinkUserIcon from "../../../../../assets/management/unlink_user.svg";
import phoneIcon from "../../../../../assets/shared/phone.svg";
import ActiveIndicator from "../../../../Shared/ActiveIndicator/ActiveIndicator";
import EmailLink from "../../../../Shared/EmailLink/EmailLink";
import styles from "./TreeStudyUserItem.module.css";

import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { useManageStudiesContext } from "../../../../../context/ManageStudiesContext";
import useAlert from "../../../../../hooks/useAlert";
import studiesService from "../../../../../services/studies.service";
import AlertModal from "../../../../Shared/AlertModal/AlertModal";
import NoDataIndicator from "../../../../Shared/NoDataIndicator/NoDataIndicator";

type TreeStudyUserItemProps = {
  item: IStudyUserTreeItem;
};
const TreeStudyUserItem = ({ item }: TreeStudyUserItemProps) => {
  const {
    userId,
    studyId,
    isLinked,
    name,
    email,
    phone,
    clinic,
    canApproveEcgs,
  } = item;

  const { loadStudies } = useManageStudiesContext();
  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();
  const [loading, setLoading] = useState(false);

  const toggleLinkClickHandler = async () => {
    setLoading(true);

    try {
      const {
        success,
        errorCode,
      } = await studiesService.toggleUsersLinkToStudy(
        [userId],
        studyId!,
        !isLinked
      );

      if (!success) {
        const { message, support } = getAlertMessageAndSupportContent(
          isLinked
            ? "ERROR.TOGGLE_USER_UNLINK_TO_STUDY"
            : "ERROR.TOGGLE_USER_LINK_TO_STUDY",
          isLinked
            ? "An error occurred while trying to unlink a user to a study.<br></br>Please try again."
            : "An error occurred while trying to link a user to a study.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return;
      }

      loadStudies({ toggleLoading: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className={`${!isLinked &&
          styles.unlinkedItem} d-flex align-items-center w-100`}
      >
        <div className={`d-flex algin-items-center ${styles.userName}`}>
          {!canApproveEcgs ? (
            <BaseIcon icon={linkedUserIcon} className={styles.headerIcon} />
          ) : (
            <Popover
              id={uuidv4()}
              hoverContent={
                <FormattedMessage
                  id="USER_DETAILS._AUTHORIZED_FOR_ECG_APPROVE"
                  defaultMessage="User authorized for approving the ECG"
                />
              }
            >
              <BaseIcon
                icon={linkedApproverUserIcon}
                className={styles.headerIcon}
              />
            </Popover>
          )}

          <span className="mx-1"></span>
          <span className="cut-text">{name}</span>
        </div>

        <div className="d-flex flex-grow-1 justify-content-between">
          <span className={styles.email}>
            <EmailLink email={email} />
          </span>

          <h5 className={`${styles.phone} mb-0 mx-1`}>
            <div className="d-flex align-items-center">
              <BaseIcon icon={phoneIcon} className={styles.phoneIcon} />
              <span className="mx-1"></span>
              <span className="cut-text">{phone ?? <NoDataIndicator />}</span>
            </div>
          </h5>

          <span className={`${styles.clinic} cut-text`}>
            {clinic ?? <NoDataIndicator />}
          </span>
        </div>

        <div
          className={`${styles.actions} d-flex align-items-center justify-content-end`}
        >
          <ActiveIndicator active={isLinked} className="mx-3" />

          <Popover
            id={uuidv4()}
            hoverContent={
              <FormattedMessage
                id={
                  isLinked ? "USER_DETAILS.DEACTIVATE" : "USER_DETAILS.ACTIVATE"
                }
                defaultMessage={isLinked ? "Deactivate" : "Activate"}
              />
            }
          >
            <Button
              className={`d-flex ${styles.button}`}
              onClick={toggleLinkClickHandler}
              disabled={loading}
            >
              {loading ? (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="font-md"
                  variant="dark"
                  style={{ width: "1.26rem", height: "1.26rem" }}
                />
              ) : (
                <BaseIcon
                  icon={isLinked ? unlinkUserIcon : linkUserIcon}
                  className={styles.icon}
                />
              )}
            </Button>
          </Popover>
        </div>
      </div>
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </>
  );
};

export default TreeStudyUserItem;
